import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {UserSource} from "../../tracking/tracking_utils/user_source";

export enum RegistrationFlowStep {
    COMPLETE_PROFILE = "COMPLETE_PROFILE",
    HOMEMATCH = "HOMEMATCH",
    THANK_YOU = "THANK_YOU",
    COMPLETED = "COMPLETED"
}

export enum RegistrationFlowVariant {
    DEFAULT = "DEFAULT",
    FAVORITES = "FAVORITES"
}

export type RegistrationMethod = "email" | "social";
export type SocialMediaType = "google" | "facebook";

export interface IRegistrationFlowPreferences {
    regions: number[];
    price?: number;
    rooms: {lower: number; upper: number};
}

export interface IRegistrationFlowState {
    isActive: boolean;
    currentStep: RegistrationFlowStep;
    needsProfileCompletion: boolean;
    registrationMethod: RegistrationMethod;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    preferences: IRegistrationFlowPreferences | null;
    variant: RegistrationFlowVariant;
}

const initialState: IRegistrationFlowState = {
    isActive: false,
    currentStep: RegistrationFlowStep.COMPLETE_PROFILE,
    needsProfileCompletion: false,
    registrationMethod: "email",
    socialMediaType: undefined,
    userSource: undefined,
    gtmSourceSection: undefined,
    preferences: null,
    variant: RegistrationFlowVariant.DEFAULT
};

export interface IStartRegistrationFlowPayload {
    needsProfileCompletion: boolean;
    registrationMethod: RegistrationMethod;
    variant?: RegistrationFlowVariant;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
}

export const registrationFlowSlice = createSlice({
    name: "registrationFlow",
    initialState,
    reducers: {
        startRegistrationFlow: (state, action: PayloadAction<IStartRegistrationFlowPayload>) => {
            state.isActive = true;
            state.needsProfileCompletion = action.payload.needsProfileCompletion;
            state.registrationMethod = action.payload.registrationMethod;
            state.variant = action.payload.variant || RegistrationFlowVariant.DEFAULT;
            state.socialMediaType = action.payload.socialMediaType;
            state.userSource = action.payload.userSource;
            state.gtmSourceSection = action.payload.gtmSourceSection;

            // Determine starting step based on whether profile completion is needed
            if (action.payload.needsProfileCompletion) {
                state.currentStep = RegistrationFlowStep.COMPLETE_PROFILE;
            } else {
                state.currentStep = RegistrationFlowStep.HOMEMATCH;
            }
        },
        nextStep: (state) => {
            switch (state.currentStep) {
                case RegistrationFlowStep.COMPLETE_PROFILE:
                    state.currentStep = RegistrationFlowStep.HOMEMATCH;
                    break;
                case RegistrationFlowStep.HOMEMATCH:
                    state.currentStep = RegistrationFlowStep.THANK_YOU;
                    break;
                case RegistrationFlowStep.THANK_YOU:
                    state.currentStep = RegistrationFlowStep.COMPLETED;
                    state.isActive = false;
                    break;
                default:
                    state.isActive = false;
            }
        },
        skipCurrentStep: (state) => {
            if (state.currentStep === RegistrationFlowStep.COMPLETE_PROFILE) {
                state.currentStep = RegistrationFlowStep.HOMEMATCH;
            }

            if (state.currentStep === RegistrationFlowStep.HOMEMATCH) {
                state.currentStep = RegistrationFlowStep.THANK_YOU;
                state.isActive = true;
            }
        },
        savePreferences: (state, action: PayloadAction<IRegistrationFlowPreferences>) => {
            state.preferences = action.payload;
        },
        closeFlow: () => {
            return initialState;
        }
    }
});

export const {startRegistrationFlow, nextStep, skipCurrentStep, savePreferences, closeFlow} = registrationFlowSlice.actions;
